import Dropdown from "components/atoms/Dropdown/Dropdown";
import FormField from "components/molecules/FormField/FormField";
import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { ReactComponent as CircleCross } from "assets/images/icons/CircleCross.svg";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import errorToast from "helpers/errorToast";
import { getAirports } from "api";
import { handleRenderAddressOption } from "helpers/schedulehelper";
import { IAirportAddress } from "../../ScheduleTypes";

// Utility function to generate options array
const generateOptions = (start: number, end: number, labelSuffix = "") =>
  Array.from({ length: end - start + 1 }, (_, i) => ({
    label: `${i + start} ${labelSuffix}`.trim(),
    value: i + start,
  }));

// Generate options for days, hours and minutes dropdowns
const daysOptions = generateOptions(0, 31, "days");
const hoursOptions = generateOptions(0, 23, "hours");
const minutesOptions = generateOptions(0, 59, "minutes");

const initialFlightObject = {
  departure_airport_code: "",
  arrival_airport_code: "",
};

function AirCharterDelivery() {
  const intl = useIntl();
  const methods = useFormContext();
  const { control, setValue, getValues } = methods;
  const { fields, append, remove } = useFieldArray({
    name: "schedule.ct_flights",
    control,
    shouldUnregister: true,
  });

  const [airportQuery, setAirportQuery] = useState<string>("");
  const [isAirportFetching, setIsAirportFetching] = useState(false);
  const [airportResults, setAirportResults] = useState<IAirportAddress[]>([]);

  // On initial load, append the initial flight object
  useEffect(() => {
    if (fields?.length === 0) {
      // When editing a quote, the flights are returned in the flights array instead of the ct_flights array
      // If that's the case, we need to append the flights to the ct_flights array
      const flights = getValues("schedule.flights");
      if (flights?.length > 0)
        flights.forEach((flight: { departure_airport_code: string; arrival_airport_code: string }) => append(flight));
      // If there are no flights at all, append the initial flight object
      else append(initialFlightObject);
    }
  }, []);

  // On airport query change, fetch the possible airports
  useEffect(() => {
    if (airportQuery.length < 2) return;
    setIsAirportFetching(true);
    const timeout = setTimeout(() => {
      getAirports(airportQuery)
        .then((res) => setAirportResults(res.data.results))
        .catch((error) => errorToast(error))
        .finally(() => setIsAirportFetching(false));
    }, 300);
    return () => clearTimeout(timeout);
  }, [airportQuery]);

  return (
    <>
      <div className="w-192">
        <FormField
          validations={{
            shouldUnregister: true,
          }}
          label={intl.formatMessage({
            id: "quote-request.costDetailAirCarterAircraftType",
            defaultMessage: "Aircraft type",
          })}
          type="textinput"
          placeholder="e.g. Boeing 747"
          field_id="schedule.ct_aircraft_type"
        />
      </div>
      <div className="w-192 mt-3">
        <FormField
          validations={{
            shouldUnregister: true,
          }}
          label={intl.formatMessage({
            id: "quote-request.costDetailAirCarterMaximumPayload",
            defaultMessage: "Maximum payload",
          })}
          type="number"
          placeholder="Enter weight and/or volume"
          field_id="schedule.ct_maximum_payload"
        />
      </div>
      <div className="mt-3">
        <label>
          <FormattedMessage
            id="quote-request.costDetailAirCarterTotalFlightDuration"
            defaultMessage="Total flight duration"
          />
        </label>
        <div className="d-flex">
          <Dropdown
            useHookForm
            name="schedule.ct_flight_duration_days"
            className="schedule-dropdown w-90"
            defaultValue={0}
            placeholder="Days"
            options={daysOptions}
          />
          <Dropdown
            useHookForm
            name="schedule.ct_flight_duration_hours"
            className="schedule-dropdown w-90 ml-3"
            placeholder="Hours"
            defaultValue={0}
            options={hoursOptions}
          />
          <Dropdown
            useHookForm
            name="schedule.ct_flight_duration_minutes"
            className="schedule-dropdown w-90 ml-3"
            placeholder="Minutes"
            defaultValue={0}
            options={minutesOptions}
          />
        </div>
      </div>
      <div className="mt-3">
        <label>
          <FormattedMessage id="quote-request.costDetailAirCarterPTC" defaultMessage="Positioning time from confirmation" />
        </label>
        <div className="d-flex">
          <Dropdown
            useHookForm
            name="schedule.ct_ptc_days"
            className="schedule-dropdown w-90"
            placeholder="Days"
            defaultValue={0}
            options={daysOptions}
          />
          <Dropdown
            useHookForm
            name="schedule.ct_ptc_hours"
            className="schedule-dropdown w-90 ml-3"
            placeholder="Hours"
            defaultValue={0}
            options={hoursOptions}
          />
          <Dropdown
            useHookForm
            name="schedule.ct_ptc_minutes"
            className="schedule-dropdown w-90 ml-3"
            placeholder="Minutes"
            defaultValue={0}
            options={minutesOptions}
          />
        </div>
      </div>
      <div className="mt-3">
        {/* ADD CHARTER FLIGHT SECTION */}
        {fields.map((flight, index) => (
          <div key={flight.id} className={index > 0 ? "mt-3" : "mt-0"}>
            <label className="font-weight-bold">
              <FormattedMessage id="general.charterFlight" defaultMessage="Charter flight" /> {index + 1}
            </label>
            <div className="mt-1 d-flex align-items-center">
              <SearchSelect
                isLoading={isAirportFetching}
                shouldUnregister
                preselect={false}
                name={`schedule.ct_flights.${index}.departure_airport_name`}
                defaultValue={getValues(`schedule.ct_flights.${index}.departure_airport_code`)}
                placeholder={intl.formatMessage({
                  id: "report.search",
                  defaultMessage: "Search",
                })}
                onInputChange={(val: string) => {
                  setAirportQuery(val);
                }}
                options={airportResults.map((option) => ({
                  id: option.id,
                  label: option.name,
                  value: option.name,
                  city_name: option.city_name,
                  country_name: option.country_name,
                  country_code: option.country_code,
                  code: option.code || option.un_locode,
                }))}
                label={intl.formatMessage({
                  id: "quote.provider.schedule.departureAirport",
                  defaultMessage: "Departure airport",
                })}
                onSelect={(item: IAirportAddress[]) => {
                  setValue(`schedule.ct_flights.${index}.departure_airport_code`, item?.[0]?.code || "");
                  setAirportResults([]);
                }}
                filterBy={["label", "city_name", "country_name", "code"]}
                handleRenderOption={handleRenderAddressOption}
                classes="w-192 mr-3"
              />
              <SearchSelect
                shouldUnregister
                preselect={false}
                name={`schedule.ct_flights.${index}.arrival_airport_name`}
                defaultValue={getValues(`schedule.ct_flights.${index}.arrival_airport_code`)}
                placeholder={intl.formatMessage({
                  id: "report.search",
                  defaultMessage: "Search",
                })}
                onInputChange={(val: string) => {
                  setAirportQuery(val);
                }}
                options={airportResults.map((option) => ({
                  id: option.id,
                  label: option.name,
                  value: option.name,
                  city_name: option.city_name,
                  country_name: option.country_name,
                  country_code: option.country_code,
                  code: option.code || option.un_locode,
                }))}
                label={intl.formatMessage({
                  id: "quote.provider.schedule.arrivalAirport",
                  defaultMessage: "Arrival airport",
                })}
                onSelect={(item: IAirportAddress[]) => {
                  setValue(`schedule.ct_flights.${index}.arrival_airport_code`, item?.[0]?.code || "");
                  setAirportResults([]);
                }}
                filterBy={["label", "city_name", "country_name", "code"]}
                handleRenderOption={handleRenderAddressOption}
                classes="w-192"
              />
              <CircleCross
                width={20}
                height={20}
                stroke="black"
                className="ml-3 mt-4 cursor-pointer"
                onClick={() => remove(index)}
              />
            </div>
          </div>
        ))}
        <p onClick={() => append(initialFlightObject)} className="text-blue-200 font-b4 cursor-pointer mt-3">
          <FormattedMessage id="quote.provider.schedule.addFlight" defaultMessage="Add flight" />
        </p>
      </div>
    </>
  );
}

export default AirCharterDelivery;
